import React, { useMemo, useState, Fragment } from 'react';
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown, ArrowUpDown, DollarSign, User, Briefcase, Building, Inbox } from 'lucide-react';
import { FundedLoan } from '../data/mockLoans';
type SortKey = 'borrowerName' | 'propertyAddress' | 'loanAmount' | 'loanType' | 'fundedDate' | 'loanOfficer' | 'paymentModel' | 'totalCommissions';
type SortDirection = 'asc' | 'desc';
interface LoansTableProps {
  loans: FundedLoan[];
  showModelColumn: boolean;
}
export function LoansTable({
  loans,
  showModelColumn
}: LoansTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('fundedDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };
  const sortedLoans = useMemo(() => {
    return [...loans].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      // Handle date comparison
      if (sortKey === 'fundedDate') {
        const aTime = new Date(aVal as string).getTime();
        const bTime = new Date(bVal as string).getTime();
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      }
      // Handle number comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      // Handle string comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [loans, sortKey, sortDirection]);
  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getRoleIcon = (role: string) => {
    if (role.includes('Loan Officer')) return <User className="w-3.5 h-3.5 text-blue-500" data-id="element-32" />;
    if (role.includes('Branch')) return <Building className="w-3.5 h-3.5 text-purple-500" data-id="element-33" />;
    return <Briefcase className="w-3.5 h-3.5 text-slate-400" data-id="element-34" />;
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: SortKey;
  }) => {
    if (sortKey !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-35" />;
    }
    return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 ml-1.5 text-blue-600" data-id="element-36" /> : <ArrowDown className="w-3 h-3 ml-1.5 text-blue-600" data-id="element-37" />;
  };
  const thClass = (align: 'left' | 'right' = 'left') => `px-6 py-4 text-${align} text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700 hover:bg-slate-100 transition-colors group border-b border-slate-200`;
  if (loans.length === 0) {
    return <div className="bg-white p-16 text-center flex flex-col items-center justify-center" data-id="element-38">
        <div className="bg-slate-50 p-4 rounded-full mb-4" data-id="element-39">
          <Inbox className="w-8 h-8 text-slate-400" data-id="element-40" />
        </div>
        <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-41">
          No loans found
        </h3>
        <p className="text-slate-500 max-w-sm mx-auto" data-id="element-42">
          There are no funded loans to display for this category in the selected
          period.
        </p>
      </div>;
  }
  const colSpan = showModelColumn ? 9 : 8;
  return <div className="overflow-x-auto" data-id="element-43">
      <table className="min-w-full divide-y divide-slate-200" data-id="element-44">
        <thead className="bg-slate-50" data-id="element-45">
          <tr data-id="element-46">
            <th scope="col" className={thClass('left')} onClick={() => handleSort('borrowerName')} data-id="element-47">
              <span className="inline-flex items-center" data-id="element-48">
                Borrower
                <SortIcon columnKey="borrowerName" data-id="element-49" />
              </span>
            </th>
            <th scope="col" className={thClass('left')} onClick={() => handleSort('propertyAddress')} data-id="element-50">
              <span className="inline-flex items-center" data-id="element-51">
                Property
                <SortIcon columnKey="propertyAddress" data-id="element-52" />
              </span>
            </th>
            <th scope="col" className={thClass('right')} onClick={() => handleSort('loanAmount')} data-id="element-53">
              <span className="inline-flex items-center justify-end" data-id="element-54">
                Loan Amount
                <SortIcon columnKey="loanAmount" data-id="element-55" />
              </span>
            </th>
            <th scope="col" className={thClass('left')} onClick={() => handleSort('loanType')} data-id="element-56">
              <span className="inline-flex items-center" data-id="element-57">
                Loan Purpose
                <SortIcon columnKey="loanType" data-id="element-58" />
              </span>
            </th>
            <th scope="col" className={thClass('left')} onClick={() => handleSort('fundedDate')} data-id="element-59">
              <span className="inline-flex items-center" data-id="element-60">
                Closed Date
                <SortIcon columnKey="fundedDate" data-id="element-61" />
              </span>
            </th>
            <th scope="col" className={thClass('left')} onClick={() => handleSort('loanOfficer')} data-id="element-62">
              <span className="inline-flex items-center" data-id="element-63">
                Loan Officer
                <SortIcon columnKey="loanOfficer" data-id="element-64" />
              </span>
            </th>
            {showModelColumn && <th scope="col" className={thClass('left')} onClick={() => handleSort('paymentModel')} data-id="element-65">
                <span className="inline-flex items-center" data-id="element-66">
                  Model
                  <SortIcon columnKey="paymentModel" data-id="element-67" />
                </span>
              </th>}
            <th scope="col" className={thClass('right')} onClick={() => handleSort('totalCommissions')} data-id="element-68">
              <span className="inline-flex items-center justify-end" data-id="element-69">
                Total Payout
                <SortIcon columnKey="totalCommissions" data-id="element-70" />
              </span>
            </th>
            <th scope="col" className="px-6 py-4 w-12 border-b border-slate-200" data-id="element-71">
              <span className="sr-only" data-id="element-72">Expand</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-100" data-id="element-73">
          {sortedLoans.map(loan => {
          const isExpanded = expandedRows.has(loan.id);
          const isFlatFee = loan.paymentModel === 'Flat Fee';
          const accentColor = isFlatFee ? 'text-indigo-600' : 'text-emerald-600';
          const bgAccent = isFlatFee ? 'bg-indigo-50' : 'bg-emerald-50';
          const borderAccent = isFlatFee ? 'border-indigo-500' : 'border-emerald-500';
          return <Fragment key={loan.id} data-id="element-74">
                <tr onClick={() => toggleRow(loan.id)} className={`
                    group hover:bg-slate-50/80 cursor-pointer transition-colors duration-150 
                    ${isExpanded ? 'bg-slate-50' : ''}
                  `} data-id="element-75">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900" data-id="element-76">
                    {loan.borrowerName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 max-w-[200px] truncate" title={loan.propertyAddress} data-id="element-77">
                    {loan.propertyAddress}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 text-right tabular-nums" data-id="element-78">
                    {formatCurrency(loan.loanAmount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-79">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                      ${loan.loanType === 'Purchase' ? 'bg-blue-50 text-blue-700 border-blue-100' : loan.loanType === 'Refinance' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-purple-50 text-purple-700 border-purple-100'}`} data-id="element-80">
                      {loan.loanType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 tabular-nums" data-id="element-81">
                    {formatDate(loan.fundedDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-700" data-id="element-82">
                    {loan.loanOfficer}
                  </td>
                  {showModelColumn && <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-83">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                        ${isFlatFee ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-84">
                        {loan.paymentModel}
                      </span>
                    </td>}
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold text-right tabular-nums ${accentColor}`} data-id="element-85">
                    {formatCurrency(loan.totalCommissions)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-86">
                    <button className={`
                        p-1 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                        ${isExpanded ? 'text-slate-600 bg-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}
                      `} aria-expanded={isExpanded} aria-label={isExpanded ? 'Collapse details' : 'Expand details'} onClick={e => {
                  e.stopPropagation();
                  toggleRow(loan.id);
                }} data-id="element-87">
                      {isExpanded ? <ChevronUp className="w-4 h-4" data-id="element-88" /> : <ChevronDown className="w-4 h-4" data-id="element-89" />}
                    </button>
                  </td>
                </tr>

                {isExpanded && <tr data-id="element-90">
                    <td colSpan={colSpan} className="p-0 border-b border-slate-200" data-id="element-91">
                      <div className={`bg-slate-50 px-6 py-6 border-l-[6px] ${borderAccent} animate-in slide-in-from-top-2 duration-200`} data-id="element-92">
                        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden max-w-5xl mx-auto" data-id="element-93">
                          <div className={`px-6 py-4 border-b border-slate-100 flex justify-between items-center ${bgAccent} bg-opacity-30`} data-id="element-94">
                            <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center" data-id="element-95">
                              <DollarSign className="w-4 h-4 mr-2 text-slate-500" data-id="element-96" />
                              Commission Breakdown
                            </h5>
                            <span className={`text-sm font-bold ${accentColor}`} data-id="element-97">
                              Total Payout:{' '}
                              {formatCurrency(loan.totalCommissions)}
                            </span>
                          </div>

                          <table className="min-w-full divide-y divide-slate-100" data-id="element-98">
                            <thead className="bg-slate-50/50" data-id="element-99">
                              <tr data-id="element-100">
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/4" data-id="element-101">
                                  Payee Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/4" data-id="element-102">
                                  Role
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-1/4" data-id="element-103">
                                  Amount
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-1/4" data-id="element-104">
                                  Note
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50" data-id="element-105">
                              {loan.payees.map((payee, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} data-id="element-106">
                                  <td className="px-6 py-3 text-sm font-medium text-slate-800" data-id="element-107">
                                    {payee.name}
                                  </td>
                                  <td className="px-6 py-3 text-sm text-slate-500 flex items-center" data-id="element-108">
                                    <span className="mr-2.5 opacity-75" data-id="element-109">
                                      {getRoleIcon(payee.role)}
                                    </span>
                                    {payee.role}
                                  </td>
                                  <td className="px-6 py-3 text-sm font-semibold text-slate-900 text-right tabular-nums" data-id="element-110">
                                    {formatCurrency(payee.commissionAmount)}
                                  </td>
                                  <td className="px-6 py-3 text-sm text-slate-500 italic text-right" data-id="element-111">
                                    {payee.commissionNote}
                                  </td>
                                </tr>)}
                              <tr className="bg-slate-50/80 border-t border-slate-200" data-id="element-112">
                                <td colSpan={2} className="px-6 py-3 text-sm font-bold text-slate-700 text-right" data-id="element-113">
                                  Total Paid
                                </td>
                                <td className={`px-6 py-3 text-sm font-bold text-right tabular-nums ${accentColor}`} data-id="element-114">
                                  {formatCurrency(loan.totalCommissions)}
                                </td>
                                <td data-id="element-115"></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>}
              </Fragment>;
        })}
        </tbody>
      </table>
    </div>;
}